import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getMember } from "../../services/Member";
import baseURL from "../../config/api";
import { formatUSPhone } from "../../utils/phoneFormatter";
import { BsFillTelephoneFill, BsArrowLeft } from "react-icons/bs";
import { MdEmail } from "react-icons/md";
import useGroup from "../../hook/useGroup";
import Cookies from "js-cookie";

const ViewFamily = () => {
    const token = Cookies.get("token-my-cdir");
    const { id } = useParams();
    const { isSuperAdmin }: any = useGroup();
    const navigate = useNavigate();
    const [data, setData] = useState<any>(null);
    const pathRoot = isSuperAdmin ? "superadmin" : "admin";

    useEffect(() => {
        if (id) {
            getMember(id, token!.toString())
                .then((res) => {
                    setData({
                        ...res,
                        imagePath: res?.imagePath
                            ? `${baseURL}commonFile/Get?name=${res.imagePath}`
                            : "/images/user-default.jpg",
                    });
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (!data) {
        return (
            <div className="min-h-screen flex justify-center items-center">
                <div className="flex flex-col items-center">
                    <div
                        className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent text-secondary"
                        role="status"
                    ></div>
                    <span className="text-black mt-2">Loading...</span>
                </div>
            </div>
        );
    }

    const editMember = (id: string) => {
        navigate(`/${pathRoot}/family/editFamily/${id}`);
    }

    const getValue = (val: any) => {
        return val && val !== "" ? val : "N/A";
    };

    return (
        <div className="min-h-screen bg-gray-50 px-3 xm:px-4 md:px-6 py-4">

            {/* Header */}
            <div className="w-full flex flex-col xm:flex-row justify-between items-start xm:items-center gap-3 mb-6">
                <div>
                    <h1 className="text-xl xm:text-2xl font-bold text-gray-800">
                        {data.familyName || "Family"}
                    </h1>
                    <p className="text-xs xm:text-sm text-gray-500">
                        Family Details
                    </p>
                </div>

                <div className="flex flex-col xm:flex-row gap-2 w-full xm:w-auto">
                    <button
                        onClick={() => editMember(data.id)}
                        className="bg-secondary hover:bg-custom-blue text-white px-4 py-2 rounded-md shadow w-full xm:w-auto"
                    >
                        Edit
                    </button>

                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center gap-2 bg-gray-500 hover:bg-gray-700 text-white px-4 py-2 rounded-md shadow w-full xm:w-auto"
                    >
                        <BsArrowLeft />
                        Back
                    </button>
                </div>
            </div>

            {/* Profile Card */}
            <div className="w-full bg-white rounded-2xl shadow-xl overflow-hidden mb-8">

                <div className="h-20 xm:h-24 bg-gradient-to-r from-secondary to-blue-400" />

                <div className="flex flex-col items-center px-4 xm:px-6 pb-6 -mt-12">
                    <div className="w-24 h-24 xm:w-28 xm:h-28 rounded-full border-4 border-white shadow-md overflow-hidden">
                        <img
                            src={data.imagePath}
                            alt="family"
                            className="w-full h-full object-cover"
                            onError={(e: any) => {
                                e.target.src = "/images/user-default.jpg";
                            }}
                        />
                    </div>

                    <h2 className="mt-4 text-lg xm:text-2xl font-bold text-gray-800 text-center break-words">
                        {getValue(data.familyName)}
                    </h2>

                    <p className="text-gray-500 text-sm xm:text-base text-center">
                        {getValue(data.firstName)} {getValue(data.lastName)}
                    </p>

                    <div className="mt-6 w-full grid grid-cols-1 xm:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-4 rounded-xl border">
                            <p className="text-secondary text-xs xm:text-sm font-semibold">Email</p>
                            <p className="text-gray-800 font-medium break-all text-sm">
                                {getValue(data.emailAddress)}
                            </p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-xl border">
                            <p className="text-secondary text-xs xm:text-sm font-semibold">Phone</p>
                            <p className="text-gray-800 font-medium text-sm">
                                {data.phoneNumber ? formatUSPhone(data.phoneNumber) : "N/A"}
                            </p>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-xl border xm:col-span-2">
                            <p className="text-secondary text-xs xm:text-sm font-semibold">Address</p>
                            <p className="text-gray-800 font-medium text-sm break-words">
                                {[data.street, data.city, data.state, data.country]
                                    .filter(Boolean)
                                    .join(", ") || "N/A"}{" "}
                                {data.zip ? `- ${data.zip}` : ""}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Members */}
            <div className="w-full">
                <h2 className="text-xl xm:text-2xl font-bold text-gray-800 mb-6 text-center">
                    Members
                </h2>

                {data.familyMembers?.length > 0 ? (
                    <div className="grid gap-4 xm:gap-6 grid-cols-1 xm:grid-cols-2 lg:grid-cols-3">

                        {data.familyMembers.map((member: any, index: number) => (
                            <div
                                key={index}
                                className="group bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                            >
                                <div className="h-14 xm:h-16 bg-gradient-to-r from-secondary to-blue-400" />

                                <div className="flex flex-col items-center px-4 xm:px-6 pb-6 -mt-10">
                                    <div className="w-16 h-16 xm:w-20 xm:h-20 rounded-full border-4 border-white shadow-md overflow-hidden">
                                        <img
                                            src={
                                                member?.filePath
                                                    ? `${baseURL}commonFile/Get?name=${member.filePath}`
                                                    : "/images/user-default.jpg"
                                            }
                                            alt="family-member"
                                            className="w-full h-full object-cover"
                                            onError={(e: any) => {
                                                e.target.src = "/images/user-default.jpg";
                                            }}
                                        />
                                    </div>

                                    <h3 className="mt-3 text-sm xm:text-lg font-semibold text-gray-800 text-center break-words">
                                        {getValue(member.firstName)} {getValue(member.lastName)}
                                    </h3>

                                    <div className="mt-4 w-full bg-gray-50 rounded-xl p-3 space-y-2 border">
                                        <div className="flex items-center gap-2 text-gray-700">
                                            <BsFillTelephoneFill size={14} />
                                            <span className="text-xs xm:text-sm font-medium">
                                                {member.phoneNumber
                                                    ? formatUSPhone(member.phoneNumber)
                                                    : "N/A"}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 text-gray-700">
                                            <MdEmail size={14} />
                                            <span className="text-xs xm:text-sm font-medium break-all">
                                                {getValue(member.emailAddress)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                    </div>
                ) : (
                    <div className="text-center text-gray-500 mt-10 text-sm">
                        No Family Members Found
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewFamily;